<template>
  <div>
    <alert
      v-if="alertVisible"
      :message="alertMessage"
      :type="alertType">
    </alert>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">{{ $t('group.users', {name: this.group.name}) }}</h1>
              <div class="header-add-button">
                <span
                  v-tooltip.left.top-start="$t('group.addUser')">
                  <font-awesome-icon
                    class="header-add-button-icon"
                    @click="showAddUserModal"
                    :icon="icon.add"/>
                </span>
              </div>
            </div>
          </grid-col>
        </grid-row>
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="permissionTable"
              :data="fetchData"
              sort-by="name"
              sort-order="asc"
              :show-caption="false"
              show-filter=false
              class="datatable"
              filter-placeholder=""
              filterNoResults="">
              <table-column
                show="name"
                header-class="text-left"
                cell-class="table-component__text-left"
                :label="$t('group.name')">
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    :icon="icon.trash"
                    @click="onRemoveUser(row)"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
            </table-component>
          </grid-col>
        </grid-row>
      </div>
    </main-layout>
    <add-user-modal
      v-if="addPermissionModal"
      v-on:addUser="addUser"
      :users="getUsersToAdd"
      @cancel="addPermissionModal=false">
    </add-user-modal>
    <ok-cancel-modal
      v-if="showConfirmationRemoveModal"
      v-on:ok="onRemoveUserConfirm"
      v-on:cancel="onRemoveUserCancel"
      :title="$t('group.removeUser', {name: userToRemove.name})"
      :okButton="$t('commons.delete')"
      :cancelButton="$t('commons.cancel')">
    </ok-cancel-modal>
  </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import Alert from '@/components/shared/alert/Alert';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faTrashAlt
} from '@fortawesome/pro-regular-svg-icons';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import BtnPill from '../../components/shared/buttons/BtnPill';
import Checkbox from '../../components/shared/forms/Checkbox';
import AddUserModal from '../../components/group/AddUserModal';
import {mapActions, mapGetters} from 'vuex';
import OkCancelModal from '../../components/shared/dialogs/OkCancelModal';
import pagination from '@/components/shared/table/pagination';
import SimpleCheckbox from '../../components/shared/forms/SimpleCheckbox';

export default {
  name: 'PermissionsPanel',
  components: {
    Alert,
    SimpleCheckbox,
    OkCancelModal,
    AddUserModal,
    Checkbox,
    MainLayout,
    BtnPill,
    FontAwesomeIcon,
    TableColumn,
    TableComponent,
    GridRow,
    GridCol
  },
  data () {
    return {
      group: undefined,
      icon: {
        trash: faTrashAlt,
        add: faPlusCircle
      },
      userToRemove: undefined,
      showConfirmationRemoveModal: false,
      addPermissionModal: false,
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      alertVisible: false,
      alertMessage: '',
      alertType: '' // error, warning, success
    };
  },
  watch: {
    getGroupUsers: {
      handler: function () {
        this.$refs.permissionTable.refresh();
      }
    },
    deep: true
  },
  created () {
    this.group = this.$route.params;
    this.retrieveUsers({ context: this });
    this.retriveGroupUsers({context: this, groupId: this.group.id});
  },
  destroyed () {
    this.clearGroupUsers();
  },
  computed: {
    ...mapGetters([
      'getUsers',
      'getGroupUsers'
    ]),
    getBreadcrumb () {
      return [
        {
          link: '/settings',
          name: this.$t('menu.settings')
        },
        {
          link: '/group/list',
          name: this.$t('group.title')
        },
        {
          link: '/group/user/list',
          name: this.$t('permission.usersTitle'),
          disabled: true
        }
      ];
    },
    getUsersToAdd () {
      const allUses = this.getUsers.filter(user => user.enabled);
      const groupUsers = this.getGroupUsers;

      return allUses.filter(user => groupUsers.map(u => u.id).indexOf(user.id) < 0);
    }
  },
  methods: {
    ...mapActions([
      'retriveGroupUsers',
      'retrieveUsers',
      'clearGroupUsers',
      'addUserIntoGroup',
      'removeUserFromGroup'
    ]),
    fetchData ({ page, filter, sort }) {
      const tablePage = pagination.createPage(
        this.getGroupUsers,
        this.tableState,
        ['name'],
        this.tablePageSize,
        { page, filter, sort });
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    addUser (user) {
      this.addPermissionModal = false;
      this.requestAddUserIntoGroup({context: this, groupId: this.group.id, user: user});
    },
    onRemoveUser (user) {
      this.userToRemove = user;
      this.showConfirmationRemoveModal = true;
    },
    onRemoveUserConfirm () {
      this.requestRemoveUserFromGroup({context: this, groupId: this.group.id, user: this.userToRemove});

      this.showConfirmationRemoveModal = false;
      this.userToRemove = undefined;
    },
    onRemoveUserCancel () {
      this.showConfirmationRemoveModal = false;
      this.userToRemove = undefined;
    },
    showAddUserModal () {
      this.addPermissionModal = true;
    },
    requestAddUserIntoGroup (payload) {
      this.addUserIntoGroup(payload).then(
        () => {
          showAlert(this, this.$t('group.successUserAdded'), 'success');
        },
        () => {
          showAlert(this, this.$t('group.errorUserAdded'), 'error');
        }
      );
    },
    requestRemoveUserFromGroup (payload) {
      this.removeUserFromGroup(payload).then(
        () => {
          showAlert(this, this.$t('group.successUserRemoved'), 'success');
        },
        () => {
          showAlert(this, this.$t('group.errorUserRemoved'), 'error');
        }
      );
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

</script>

<style lang="scss">
@import "src/stylesheets/variables";

#header {
  display: inline-block;
  width: 100%;
  line-height: 1rem;
  h1 {
    margin-bottom: 0;
    margin-right: $sp-margin;
    padding-right: $sp-margin;
  }
}

.header-title {
  float: left;
}

.header-add-button {
  float: right;
  cursor: pointer;
}

.header-add-button-icon {
  font-size: 3rem;
  color: $secondary;
}

</style>



// WEBPACK FOOTER //
// src/layouts/groups/GroupUserList.vue